import React, { useRef } from 'react';

/**
 * Calendar Date of Birth Input Component.
 * Uses a single standard calendar picker with min/max constraints, custom styling,
 * vertical divider line, React-Select styled dropdown chevron indicator,
 * click-to-open calendar trigger, and real-time onChange/onBlur handling.
 */
export default function DobFields({
  value = '',
  onChange,
  onBlur,
  name = 'dob',
  disabled = false,
  min,
  max,
  title,
  className = '',
  inputClassName = '',
  'data-table': dataTable,
}) {
  const inputRef = useRef(null);

  const handleChange = (e) => {
    const val = e.target.value;
    const synthetic = {
      target: {
        name,
        value: val,
        dataset: dataTable ? { table: dataTable } : {},
      },
    };
    if (typeof onChange === 'function') {
      onChange(synthetic);
    }
  };

  const handleBlur = (e) => {
    const val = e.target.value;
    const synthetic = {
      target: {
        name,
        value: val,
        dataset: dataTable ? { table: dataTable } : {},
      },
    };
    if (typeof onBlur === 'function') {
      onBlur(synthetic);
    }
  };

  const openCalendar = () => {
    if (disabled || !inputRef.current) return;
    if (typeof inputRef.current.showPicker === 'function') {
      try {
        inputRef.current.showPicker();
      } catch (err) {
        inputRef.current.focus();
      }
    } else {
      inputRef.current.focus();
    }
  };

  return (
    <div className={`dob-calendar-wrapper ${className}`.trim()} title={title}>
      <div
        className={`dob-input-container ${disabled ? 'disabled' : ''}`.trim()}
        onClick={openCalendar}
      >
        <input
          ref={inputRef}
          type="date"
          name={name}
          value={value || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          min={min}
          max={max}
          className={`dob-date-input ${inputClassName}`.trim()}
          aria-label="Date of Birth"
          placeholder="YYYY-MM-DD"
        />
        <div className="dob-indicator-separator" aria-hidden="true" />
        <div className="dob-dropdown-indicator" aria-hidden="true">
          <svg
            width="14"
            height="14"
            viewBox="0 0 20 20"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"
              fill="currentColor"
            />
          </svg>
        </div>
      </div>
      <style>{`
        .dob-calendar-wrapper {
          width: 100%;
          margin-top: 3px;
        }
        .dob-input-container {
          position: relative;
          display: flex;
          align-items: center;
          width: 100%;
          min-height: 42px;
          height: 42px;
          box-sizing: border-box;
          border: 1px solid #ccc;
          border-radius: 12px;
          background: #fff;
          padding: 0 12px 0 14px;
          cursor: pointer;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .dob-input-container:hover:not(.disabled) {
          border-color: #b3b3b3;
        }
        .dob-input-container:focus-within:not(.disabled) {
          border-color: #e53d5c;
          box-shadow: 0 0 0 2px rgba(229, 61, 92, 0.12);
        }
        .dob-input-container.disabled {
          background: #f3f4f6;
          border-color: #e5e7eb;
          cursor: not-allowed;
          opacity: 0.75;
        }
        .dob-date-input {
          flex: 1 1 auto;
          width: 100%;
          height: 100% !important;
          min-height: 100% !important;
          border: none !important;
          outline: none !important;
          background: transparent !important;
          font-size: 15px !important;
          color: #333 !important;
          font-family: inherit !important;
          padding: 0 !important;
          margin: 0 !important;
          box-shadow: none !important;
          cursor: pointer !important;
          -webkit-appearance: none;
          appearance: none;
        }
        .dob-date-input::-webkit-calendar-picker-indicator {
          display: none !important;
          -webkit-appearance: none !important;
          opacity: 0 !important;
          width: 0 !important;
          height: 0 !important;
          padding: 0 !important;
          margin: 0 !important;
        }
        .dob-date-input::-webkit-inner-spin-button,
        .dob-date-input::-webkit-clear-button {
          display: none !important;
          -webkit-appearance: none !important;
        }
        .dob-indicator-separator {
          width: 1px;
          height: 20px;
          background-color: #cccccc;
          margin: 0 10px 0 8px;
          flex-shrink: 0;
        }
        .dob-dropdown-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          color: #808080;
          flex-shrink: 0;
          padding: 2px;
          transition: color 0.15s ease;
        }
        .dob-input-container:hover .dob-dropdown-indicator {
          color: #555555;
        }
      `}</style>
    </div>
  );
}

